/**
 * ProofViewerDialog
 *
 * A modal dialog that renders a termination task's proof image (uploaded for
 * the `social_insurance_closing` task type's `processing` or `done` slot) at
 * full size, fetched from R2 via {@link fetchTerminationProofBlob}.
 *
 * Mirrors `employees/DocumentViewerDialog.tsx`'s blob-fetch/object-URL
 * lifecycle, simplified for the image-only case (proof uploads are always
 * validated as images server-side, so no PDF branch is needed).
 *
 * @module terminations/ProofViewerDialog
 */

import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { fetchTerminationProofBlob, type TerminationProofSlot } from '../dataProvider';

export interface ProofViewerDialogProps {
    /** Whether the dialog is open. */
    open: boolean;
    /** Called when the user requests to close the dialog. */
    onClose: () => void;
    /** The numeric `id` of the termination checklist item. */
    taskId: number | string;
    /** Which proof image to fetch and display. */
    slot: TerminationProofSlot;
    /** Human-readable label used as the dialog title. */
    label: string;
}

/**
 * Modal viewer for termination proof images.
 *
 * Fetches the image blob when the dialog opens, shows a spinner while
 * loading, an error message if the fetch fails, and revokes the object URL
 * on close/unmount to avoid leaking memory.
 */
const ProofViewerDialog: React.FC<ProofViewerDialogProps> = ({ open, onClose, taskId, slot, label }) => {
    const [blobUrl, setBlobUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const blobUrlRef = useRef<string | null>(null);

    const revokeCurrent = () => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    };

    useEffect(() => {
        if (!open) return;

        let cancelled = false;
        setIsLoading(true);
        setError(null);
        setBlobUrl(null);

        fetchTerminationProofBlob(taskId, slot)
            .then((url) => {
                if (cancelled) {
                    if (url) URL.revokeObjectURL(url);
                    return;
                }
                revokeCurrent();
                blobUrlRef.current = url;
                setBlobUrl(url);
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Failed to load proof image');
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, taskId, slot]);

    const handleClose = () => {
        revokeCurrent();
        setBlobUrl(null);
        setError(null);
        onClose();
    };

    useEffect(() => {
        return () => {
            revokeCurrent();
        };
    }, []);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            data-testid="proof-viewer-dialog"
            PaperProps={{ sx: { minHeight: '60vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" component="span">
                    {label}
                </Typography>
                <IconButton
                    aria-label="Close proof viewer"
                    onClick={handleClose}
                    size="small"
                    data-testid="proof-viewer-close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    overflow: 'hidden',
                }}
            >
                {isLoading && <CircularProgress data-testid="proof-viewer-loading" />}

                {!isLoading && error && (
                    <Typography color="error" data-testid="proof-viewer-error">
                        {error}
                    </Typography>
                )}

                {!isLoading && !error && blobUrl && (
                    <Box
                        sx={{
                            maxWidth: '100%',
                            maxHeight: '75vh',
                            overflow: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                        }}
                    >
                        <img
                            src={blobUrl}
                            alt={label}
                            style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
                            data-testid="proof-viewer-image"
                        />
                    </Box>
                )}

                {!isLoading && !error && !blobUrl && (
                    <Typography color="text.secondary" data-testid="proof-viewer-empty">
                        No proof image uploaded for this step.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ProofViewerDialog;
